import { CoreCard, CoreContainer } from '@ingenico/react-ui';
import { CoreTypography, CoreTypographyVariant } from '@ingenico/react-ui/core-typography';
import { AutocompleteControl, TextFieldControl } from '@ingenico/react-ui/form-section';
import { EditorMode, MarkDownEditorCtrl } from '@ingenico/react-ui/mark-down-editor';
import { ALL_LNGS_NAMESPACES } from '@ingenico/util-i18n-config';
import { toString } from 'lodash-es';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { SyncConfigurationEntityOption } from './syncConfigurationPageContent.model';
import { SYNC_CONFIGURATION_PAGE_CONTENT } from './SyncConfigurationPageContent.util';
import { useSyncConfigurationPageContent } from './useSyncConfigurationPageContent.vm';
import styles from './SyncConfigurationPageContent.module.scss';
import { SyncConfigurationButton } from '../sync-configuration-button/SyncConfigurationButton.ctrl';

export function SyncConfigurationPageContent(): ReactNode {
  const { t } = useTranslation([ALL_LNGS_NAMESPACES.COMMON]);
  const {
    entityOptions,
    selectedEntity,
    handleEntityChange,
    idsSectionTitle,
    isDeviceEntitySelected,
    singleId,
    handleSingleIdChange,
    deviceIds,
    handleDeviceIdsChange,
  } = useSyncConfigurationPageContent();

  return (
    <CoreContainer customMainClassName={styles.container}>
      <CoreCard className={styles.form__container}>
        <div className={styles.section}>
          <CoreTypography variant={CoreTypographyVariant.Title01Medium}>
            {t('syncConfigurationPage.selectEntity.title')}
          </CoreTypography>
          <div className={styles.section__field}>
            <AutocompleteControl<SyncConfigurationEntityOption, false, true, false>
              disableClearable
              getOptionLabel={(option) => (typeof option === 'string' ? option : option.label)}
              input={{
                component: 'Autocomplete',
                label: toString(t('syncConfigurationPage.selectEntity.fieldLabel')),
                name: 'entity',
              }}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              onChange={handleEntityChange}
              options={entityOptions}
              value={selectedEntity}
            />
          </div>
        </div>

        <div className={styles.section}>
          <CoreTypography variant={CoreTypographyVariant.Title01Medium}>{idsSectionTitle}</CoreTypography>
          <div className={styles.section__field}>
            {isDeviceEntitySelected ? (
              <AutocompleteControl<string, true, true, true>
                disableClearable
                freeSolo
                input={{
                  component: 'Autocomplete',
                  label: toString(t('syncConfigurationPage.ids.fieldLabel')),
                  name: 'deviceIds',
                }}
                multiple
                onChange={handleDeviceIdsChange}
                options={[]}
                value={deviceIds}
              />
            ) : (
              <TextFieldControl
                input={{
                  component: 'Text',
                  label: toString(t('syncConfigurationPage.ids.fieldLabel')),
                  name: 'id',
                }}
                onChange={handleSingleIdChange}
                value={singleId}
                slotProps={{
                  inputLabel: { shrink: true },
                }}
              />
            )}
          </div>
        </div>

        <div className={styles.section}>
          <CoreTypography variant={CoreTypographyVariant.Title01Medium}>
            {t('syncConfigurationPage.triggerAction.title')}
          </CoreTypography>
          <div className={styles.section__field}>
            <SyncConfigurationButton
              selectedEntity={selectedEntity?.value}
              id={isDeviceEntitySelected ? deviceIds : singleId}
            />
          </div>
        </div>
      </CoreCard>
      <CoreCard className={styles.markdownContainer}>
        <MarkDownEditorCtrl content={SYNC_CONFIGURATION_PAGE_CONTENT} mode={EditorMode.Preview} />
      </CoreCard>
    </CoreContainer>
  );
}
